import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Code2, KeyRound, Copy, Trash2, Loader2, BookOpen, ShieldCheck, Plus } from "lucide-react";

const DeveloperKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [allowWrite, setAllowWrite] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // full secret shown once

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/developer/keys");
      setKeys(data || []);
    } catch (e) {
      toast.error("Please log in to manage API keys");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give your key a name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/developer/keys", {
        name: name.trim(),
        scopes: allowWrite ? ["read", "write"] : ["read"],
      });
      setNewKey(data);
      setName("");
      setAllowWrite(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create key");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key_id) => {
    if (!window.confirm("Revoke this key? Any integration using it will stop working immediately.")) return;
    try {
      await api.post(`/developer/keys/${key_id}/revoke`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error("Could not revoke key");
    }
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const activeKeys = keys.filter((k) => !k.revoked);
  const revokedKeys = keys.filter((k) => k.revoked);

  return (
    <div className="heritage-canvas min-h-screen">
      <SEO title="Developer API — AfriMarket" description="Manage your AfriMarket API keys." path="/dashboard/developer" />
      <div className="container-narrow py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-overline text-[#C05A3C] mb-2 flex items-center gap-2"><Code2 className="w-4 h-4" /> Developer API</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">API keys</h1>
            <p className="text-muted-foreground mt-2 font-sans-ui">Connect partners, apps and integrations to your AfriMarket catalogue and orders.</p>
          </div>
          <Link to="/developers" data-testid="developer-docs-link">
            <Button variant="outline" className="rounded-md"><BookOpen className="w-4 h-4 mr-2" /> Read the API docs</Button>
          </Link>
        </div>

        {/* Full key reveal (shown once) */}
        {newKey && (
          <Card className="p-5 mb-8 bg-[#1A2024] text-white border-transparent" data-testid="new-key-reveal">
            <div className="flex items-center gap-2 font-semibold mb-2"><ShieldCheck className="w-5 h-5 text-[#D9923B]" /> Your new API key</div>
            <p className="text-sm text-white/70 mb-3">Copy it now — for your security we can’t show it again.</p>
            <div className="flex items-center gap-2 bg-black/30 rounded-md p-3">
              <code className="text-sm break-all flex-1" data-testid="new-key-value">{newKey.api_key}</code>
              <Button size="sm" onClick={() => copy(newKey.api_key)} className="bg-[#C05A3C] hover:bg-[#D46B4E] shrink-0" data-testid="copy-new-key">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="mt-3 text-white/70 hover:text-white hover:bg-white/10" onClick={() => setNewKey(null)} data-testid="dismiss-new-key">Done</Button>
          </Card>
        )}

        {/* Create key */}
        <Card className="p-5 mb-8 bg-white border-black/10" data-testid="create-key-card">
          <div className="font-semibold mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-[#C05A3C]" /> Create a new key</div>
          <form onSubmit={create} className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Input
              data-testid="new-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Key name (e.g. Partner store, Mobile app)"
              className="flex-1"
            />
            <label className="flex items-center gap-2 text-sm select-none cursor-pointer" data-testid="write-scope-row">
              <Switch checked={allowWrite} onCheckedChange={setAllowWrite} data-testid="write-scope-toggle" />
              Allow write (create/edit listings & orders)
            </label>
            <Button type="submit" disabled={creating} className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md shrink-0" data-testid="create-key-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Generate key
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-3">Read-only keys can browse the catalogue. Write keys can also publish and manage your own listings and orders.</p>
        </Card>

        {/* Active keys */}
        <div className="mb-2 label-overline text-muted-foreground">Active keys</div>
        {loading ? (
          <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : activeKeys.length === 0 ? (
          <Card className="p-8 text-center bg-white border-black/10 mb-8" data-testid="no-keys">
            <KeyRound className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No API keys yet. Create one above to get started.</p>
          </Card>
        ) : (
          <div className="space-y-3 mb-8">
            {activeKeys.map((k) => (
              <Card key={k.key_id} className="p-4 bg-white border-black/10 flex flex-wrap items-center gap-3" data-testid={`key-row-${k.key_id}`}>
                <div className="w-10 h-10 rounded-lg bg-[#1A2024]/10 grid place-items-center shrink-0"><KeyRound className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {k.name}
                    {k.scopes?.includes("write") ? (
                      <Badge className="bg-[#C05A3C] text-white">read + write</Badge>
                    ) : (
                      <Badge variant="outline">read only</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <code>{k.prefix}…</code> · {k.request_count || 0} requests · {k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleString()}` : "never used"}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => revoke(k.key_id)} className="text-red-600 border-red-200 hover:bg-red-50 shrink-0" data-testid={`revoke-key-${k.key_id}`}>
                  <Trash2 className="w-4 h-4 mr-1" /> Revoke
                </Button>
              </Card>
            ))}
          </div>
        )}

        {revokedKeys.length > 0 && (
          <>
            <div className="mb-2 label-overline text-muted-foreground">Revoked</div>
            <div className="space-y-2 mb-8 opacity-60">
              {revokedKeys.map((k) => (
                <Card key={k.key_id} className="p-3 bg-white border-black/10 flex items-center gap-3 text-sm">
                  <code className="text-xs">{k.prefix}…</code>
                  <span className="flex-1">{k.name}</span>
                  <Badge variant="outline">revoked</Badge>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeveloperKeys;
